import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type CompanyModel from './company.ts';
import type { CsvSchema } from '../types/csv-schema.ts';
import { Type } from '@warp-drive/core-types/symbols';
import type PslfGroup from './pslf-group.ts';
import type CompanyLpnSetting from './company-lpn-setting.ts';

export type CompanySettingEnum = {
  [key: string]: {
    name: string;
    partnerLabel: string;
  }[];
};

export type CompanySettingInvoicing = {
  invoiceDay: number;
  netDueDays: number;
  requiresApproval: boolean;
  splitByCostCenter: boolean;
  serviceFeePoNumber: string;
  wellnessServiceFee: number;
  contributionPoNumber: string;
  autoApproveAfterNDays: number;
  contributionServiceFee: number;
  adoptionFloorParticipantCount: number;
  adoptionFloorParticipantAmount: number;
  billAllParticipantsForWellness: boolean;
};

export type CompanySettingQuicksight = {
  dashboards: {
    id: string;
    active: boolean;
    custom: boolean;
    titleKey: string;
    companyId: string;
    reportType: string[];
  }[];
};

type Address = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
};

export type PslfModuleEmployer = {
  services: string[];
  isPolitical: boolean;
  isTaxExempt: boolean;
  isGovernment: boolean;
  isNotForProfit: boolean;
  legalName: string;
  fein: string;
  website: string;
  address: Address;
};

export type PslfModule = {
  groups: PslfGroup[];
  enabled: boolean;
  employer: PslfModuleEmployer;
  enabledOn: string;
  isNonPartnerEmployer: boolean;
  isEnabledPriorEmployer: boolean;
  isNonPartnerApprover: boolean;
  defaultApproverEmail?: string;
};

export type CompanySettingModules = {
  externalSpendDescriptor?: string;
  pslf?: PslfModule;
  syf?: { isEnabled: boolean };
  support?: {
    faqs: { isEnabled: boolean };
    zendesk: { isEnabled: boolean };
    tioEmail: { isEnabled: boolean };
    tioPhone: { isEnabled: boolean };
    partnerEmail?: string;
    partnerPhone?: string;
  };
};

export type CompanySettingTuitionAssistance = {
  taOnlyPlanId: string;
  isTaMentoringEnabled: boolean;
  isTaOnly: boolean;
  isTaEnabled?: boolean;
  eligibilityWaitingPeriodDays?: number;
  isDependentProgramsEnabled?: boolean;
  isDirectPayEnabled?: boolean;
  isPartnerMessagingEnabled?: boolean;
  isPartnerMessagingEmailEnabled?: boolean;
  enableAgentRecommendations?: boolean;
  hideAnnualBenefitsCard?: boolean;
};

// These are technically all optional, but we should make a model
// that would give reasonable defaults.
export type CompanySettingRegister = {
  'is-email-register-allowed': boolean;
  'show-countdown': boolean;
  'is-payroll-id-and-last-name-allowed': boolean;
  'require-custom-terms': boolean;
  'custom-terms-text': string;
  'is-self-register-allowed': boolean;
  'self-register-domain': string;
};

export type EmployeeCustomAttribute = {
  field: string;
  note: string;
  required: boolean;
  custom: boolean;
  postProcess?: string;
  datatype?: string;
  comesAs?: string;
  externalServiceTags?: string[];
};

export type EmailOverrides = {
  'contribution-invitation'?: string;
  'wellness-invitation'?: string;
  'contribution-action-reminder'?: string;
  'wellness-action-reminder'?: string;
  'wellness-registration'?: string;
  'send-auto-eligible-today-email'?: boolean;
  'send-auto-two-week-before-eligible'?: boolean;
};

export type LandingPage = {
  isEnabled: boolean;
  title: string;
  html: string;
  buttonText: string;
  externalLoginUrl: string;
};
export type CompanySettingFiscalYear = {
  'start-month': number;
};

export type TasPaymentSchema = {
  file_schema: CsvSchema;
  file_filter?: string;
  file_name?: string;
};

export type TasReportSchema = {
  query: string;
  file_name?: string;
};

export type TasPaymentSettings = {
  processing?: {
    groupby?: string;
    combine_courses?: boolean;
    check_taxes?: boolean;
    taxes?: {
      tax_limit: number;
      split: boolean;
      tax_exempt_code?: string;
      non_tax_exempt_code?: string;
    };
  };
  tas_pay_deactivated_employees?: boolean;
  'tas-payment-schemas'?: TasPaymentSchema[];
  'tas-report-schemas'?: TasReportSchema[];
  'send-to-sftp'?: boolean;
  'sftp-path'?: string;
  'send-to-email'?: boolean;
  'to-emails'?: string[];
};

export type LpnConfig = {
  cappedTuition?: boolean;
  isEnabled?: boolean;
  employerRecommended?: boolean;
  hasDiscount?: boolean;
  discountPercent?: string;
};

export type LpnSettings = {
  isEnabled?: boolean;
  [key: string]: LpnConfig | boolean | undefined;
};

export type ReturnToPartnerUrl = {
  base: string;
  subdomain?: string;
  path?: string;
};

export type PersistentEmployeesConfig = {
  enabled?: boolean;
  active_length?: string;
};

export type ProcessConfig = {
  persistent_employees?: PersistentEmployeesConfig;
  [key: string]: unknown;
};

export default class CompanySettingModel extends Model {
  @attr declare allowFamilyPlan: boolean;
  @attr declare eligibilityDefault: Record<string, unknown>;
  @attr declare emailOverrides: EmailOverrides;
  @attr declare employeeCustomAttributes: EmployeeCustomAttribute[];
  @attr declare employeePortal529Tab: string;
  @attr declare employeePortalTab: string;
  @attr declare enums: CompanySettingEnum;
  @attr declare extraAuthVerification: boolean;
  @attr declare gradvisorDomain: string;
  @attr declare idProvider: string;
  @attr declare imputedIncome: Record<string, unknown>;
  @attr declare invoicing: CompanySettingInvoicing;
  @attr declare isDefault: boolean;
  @attr declare isGlobal: boolean;
  @attr declare landingPage: LandingPage;
  @attr declare loanEligibility: Record<string, unknown>;
  @attr declare modules: CompanySettingModules;
  @attr declare newDesign: boolean;
  @attr declare partnerType: string;
  @attr declare payrollCustomText: string;
  @attr declare provider: string;
  @attr declare quickSight: CompanySettingQuicksight;
  @attr declare register: CompanySettingRegister;
  @attr declare registerPageCopy: string;
  @attr declare returnToPartnerUrl: ReturnToPartnerUrl;
  @attr declare security: Record<string, unknown>;
  @attr declare sftpUserName: string;
  @attr declare ssoAuthUrl: string;
  @attr declare subDomain: string;
  @attr declare tuitionAssistance: CompanySettingTuitionAssistance;
  @attr declare tuitionmanagerAccount: string;
  @attr declare fiscalYear: CompanySettingFiscalYear;
  @attr declare tasPaymentSettings: TasPaymentSettings;
  @attr declare lpn: LpnSettings;
  @attr declare processConfig: ProcessConfig;

  @belongsTo('company', { async: true, inverse: 'companySetting' })
  declare company: CompanyModel;

  @hasMany('company-lpn-setting', { async: true, inverse: 'companySetting' })
  declare companyLpnSetting: CompanyLpnSetting[];

  get employmentTypes() {
    return this.enums['EmploymentType'] || [];
  }

  get partnerUrlString() {
    const url = this.returnToPartnerUrl;
    return url ? `https://${url.subdomain}.${url.base}${url.path ? `/${url.path}` : ''}` : null;
  }

  [Type] = 'company-setting' as const;
}
